const PDF_CONTENT_TYPES = new Set(["application/pdf", "application/x-pdf"]);
const GENERIC_CONTENT_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
]);

const normalizeContentType = (value) =>
  String(value || "")
    .split(";", 1)[0]
    .trim()
    .toLowerCase();

const extensionOf = (name) => {
  const match = String(name || "")
    .trim()
    .match(/\.([^.]+)$/);
  return match?.[1]?.toLowerCase() || "";
};

// The loan-document model (loan_files.py `LoanDocument`) carries the original
// name three ways. `file_display_name` is what people RENAME — and a rename can
// drop the extension — so it is checked last; `file_storage_name` and
// `file_name_as_loaded` are set at upload and keep the real one.
const documentExtension = (doc) => {
  const explicit = String(doc?.file_extension || "")
    .trim()
    .toLowerCase()
    .replace(/^\./, "");
  if (explicit) return explicit;

  return (
    extensionOf(doc?.file_storage_name) ||
    extensionOf(doc?.file_name_as_loaded) ||
    extensionOf(doc?.file_display_name || doc?.fileName || doc?.name)
  );
};

/**
 * Ocrolus bank-statement submissions in this portal are PDF-only.
 * Prefer the stored MIME type when it is specific; older documents sometimes
 * carry only an extension or a generic octet-stream content type, so those
 * fall back to the filename/explicit extension.
 */
export const isOcrolusSupportedDocument = (doc) => {
  const contentType = normalizeContentType(doc?.content_type);
  if (!GENERIC_CONTENT_TYPES.has(contentType)) {
    return PDF_CONTENT_TYPES.has(contentType);
  }
  return documentExtension(doc) === "pdf";
};

export const OCROLUS_PDF_ONLY_MESSAGE =
  "Ocrolus accepts PDF bank statements only. Convert PNG, JPEG, and other files to PDF before sending.";

/**
 * Split a batch into what Ocrolus will take and what it will reject. The send
 * hook runs this as the LAST line of defence for every caller — the surfaces
 * that offer the action (Ocrolus tab, Uploaded bulk bar) also gate up front so
 * a non-PDF can't be selected, but a caller that doesn't (the legacy Files tab,
 * anything future) still never reaches the backend with one.
 */
export const partitionOcrolusDocuments = (docs) => {
  const list = Array.isArray(docs) ? docs : [];
  const supported = list.filter(isOcrolusSupportedDocument);
  const unsupported = list.filter((d) => !isOcrolusSupportedDocument(d));
  return { supported, unsupported };
};

/** Display name for a rejected file in a warning — never blank. */
export const ocrolusDocumentLabel = (doc) =>
  doc?.file_display_name ||
  doc?.file_name_as_loaded ||
  doc?.file_storage_name ||
  doc?.fileName ||
  "Untitled document";

